import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import HistoryImg from "../../assets/history.jpg";
import {
  FaShip,
  FaGlobeAmericas,
  FaBox,
  FaBuilding,
} from "react-icons/fa";

const History = () => {
  const timelineEvents = [
    {
      year: "1969",
      title: "The Beginning",
      icon: <FaBuilding />,
      description:
        "Established in Hamburg, Germany and Toronto, Canada by Peter K. Laser. Managed and operated in private ownership by the founder until 2019.",
      highlights: [
        "Break Bulk | Charter services",
        "20-ft., 35-ft., 40-ft. containers",
        "Transatlantic trade: Hamburg/Bremerhaven ↔ Montreal/Toronto/New York",
      ],
    },
    {
      year: "1975",
      title: "US Expansion",
      icon: <FaShip />,
      description:
        "Established TRANSWORLD SHIPPING (USA) Inc. Pioneered lump-sum/flat rates in container door-to-door services.",
      highlights: [
        "NVO | NVOCC services from Le Havre/Hamburg-Range to Houston, LA, Oakland, Seattle, Vancouver",
        "Pioneer for flat rates in container door-to-door services",
        "First carrier to offer through bills of lading for rail, vessel, barge, heavy loads",
      ],
    },
    {
      year: "1985",
      title: "Container Line Launch",
      icon: <FaBox />,
      description:
        "Established SEAGATE CONTAINER LINE Inc. First NVO carrier to offer reefer services in combined transportation.",
      highlights: [
        "Eastbound services from Tokyo/Kobe to US West Coast",
        "Maxi-Bridge Services from Seattle to Chicago & New York",
        "First NVO carrier to offer reefer services (20-ft., 40-ft., 40 HC reefer containers)",
      ],
    },
    {
      year: "2000",
      title: "Asia Pacific Growth",
      icon: <FaGlobeAmericas />,
      description:
        "Established TWS DRAGON CONTAINER LINE, expanding into Asian markets.",
      highlights: [
        "Eastbound container traffic from Shanghai/Qingdao to US West Coast & Europe",
        "Blocktrain feeder services to Czech Republic, Austria, Hungary",
        "Eastern European distribution centers established",
      ],
    },
    {
      year: "2007",
      title: "Asian Headquarters",
      icon: <FaBuilding />,
      description:
        "Established TRANSWORLD SHIPPING (ASIA) LTD. Expanded intermodal services across North America.",
      highlights: [
        "NVO | NVOCC services from China to Canada-USA-Mexico",
        "Intermodal services to all major US destinations",
        "Distribution Terminals: Seattle, Oakland, Long Beach, San Diego",
      ],
    },
    {
      year: "2010",
      title: "Pacific Regional HQ",
      icon: <FaGlobeAmericas />,
      description:
        "Transworld Shipping (H.K.) Ltd. established as Pacific Regional Headquarters.",
      highlights: [
        "Pacific Trade Lanes: Japan, Philippines, Singapore, Taiwan, Korea",
        "Inbound services to Seattle, Tacoma, Prince Rupert",
        "Temperature-controlled services to US West Coast ports",
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Our History | Trans World Shipping Company</title>
        <meta
          name="description"
          content="Trans World Shipping Company has a rich history of providing exceptional logistics and transportation services, evolving over the years to meet the changing needs of our clients and the industry."
        />
        <link rel="canonical" href="https://transworldshipping.com/history" />
      </Helmet>

      <TheHistory>
        <div className="history-hero">
          <img src={HistoryImg} alt="Trans World Shipping History" />
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <h1>Our History</h1>
            <p>Over 50 years of logistics excellence</p>
          </div>
        </div>

        <div className="history-container">
          <div className="intro-section">
            <h2>A Legacy of Innovation</h2>
            <div className="header-line"></div>
            <p>
              From our humble beginnings in Hamburg and Toronto to becoming a
              global logistics leader, Trans World Shipping has consistently
              pioneered innovative solutions that shape the industry. Our
              journey is marked by continuous growth, strategic expansion, and
              unwavering commitment to excellence.
            </p>
          </div>

          <div className="timeline">
            {timelineEvents.map((event, index) => (
              <div
                className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}
                key={index}
              >
                <div className="timeline-marker">
                  <div className="timeline-icon">{event.icon}</div>
                  <div className="timeline-year">{event.year}</div>
                </div>
                <div className="timeline-content">
                  <h3>{event.title}</h3>
                  <p className="timeline-description">{event.description}</p>
                  <ul className="timeline-highlights">
                    {event.highlights.map((highlight, idx) => (
                      <li key={idx}>{highlight}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="legacy-section">
            <div className="legacy-content">
              <h3>Continuing the Journey</h3>
              <p>
                Today, Trans World Shipping stands as a testament to visionary
                leadership and operational excellence. Our commitment to
                innovation, customer service, and global connectivity continues
                to drive us forward, building on the strong foundation
                established by our founder Peter K. Laser in 1969.
              </p>
              <div className="legacy-stats">
                <div className="stat">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Years of Excellence</span>
                </div>
                <div className="stat">
                  <span className="stat-number">25+</span>
                  <span className="stat-label">Global Locations</span>
                </div>
                <div className="stat">
                  <span className="stat-number">3</span>
                  <span className="stat-label">Continents Served</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TheHistory>
    </>
  );
};

const TheHistory = styled.div`
  width: 100%;
  background: #ffffff;

  .history-hero {
    position: relative;
    width: 100%;
    height: 50vh;
    min-height: 400px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        135deg,
        rgba(11, 30, 51, 0.85) 0%,
        rgba(0, 0, 0, 0.6) 100%
      );
    }

    .hero-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: white;
      width: 100%;
      padding: 0 2rem;
      z-index: 1;

      h1 {
        font-size: 3rem;
        font-weight: 700;
        margin-bottom: 1rem;
      }

      p {
        font-size: 1.2rem;
        opacity: 0.9;
      }
    }
  }

  .history-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 4rem 2rem;
  }

  .intro-section {
    text-align: center;
    margin-bottom: 4rem;

    h2 {
      font-size: 2rem;
      color: #0b1e33;
      margin-bottom: 1rem;
      font-weight: 700;
    }

    .header-line {
      width: 60px;
      height: 3px;
      background: #d4af37;
      margin: 0 auto 1.5rem;
    }

    p {
      font-size: 1.05rem;
      color: #4a5568;
      max-width: 800px;
      margin: 0 auto;
      line-height: 1.7;
    }
  }

  .timeline {
    position: relative;
    margin: 3rem 0 4rem;

    &::before {
      content: "";
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      width: 2px;
      height: 100%;
      background: linear-gradient(to bottom, #d4af37, rgba(212, 175, 55, 0.3));
    }
  }

  .timeline-item {
    position: relative;
    margin-bottom: 3rem;
    width: 100%;

    &.left {
      .timeline-content {
        margin-right: auto;
        margin-left: 0;
        text-align: right;

        ul {
          text-align: right;
        }
      }

      .timeline-marker {
        left: 50%;
        transform: translateX(-50%);
      }
    }

    &.right {
      .timeline-content {
        margin-left: auto;
        margin-right: 0;
        text-align: left;

        ul {
          text-align: left;
        }
      }

      .timeline-marker {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }

  .timeline-marker {
    position: absolute;
    top: 0;
    width: 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 2;
  }

  .timeline-icon {
    width: 50px;
    height: 50px;
    background: #0b1e33;
    border: 2px solid #d4af37;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d4af37;
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    background: white;
  }

  .timeline-year {
    font-weight: 800;
    color: #d4af37;
    font-size: 1.1rem;
  }

  .timeline-content {
    width: calc(50% - 60px);
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
      border-color: #d4af37;
    }

    h3 {
      font-size: 1.3rem;
      color: #0b1e33;
      margin-bottom: 0.75rem;
      font-weight: 700;
    }

    .timeline-description {
      font-size: 0.95rem;
      color: #4a5568;
      margin-bottom: 1rem;
      line-height: 1.5;
    }

    .timeline-highlights {
      list-style: none;
      padding: 0;

      li {
        font-size: 0.85rem;
        color: #718096;
        padding: 0.25rem 0;
        position: relative;
        padding-left: 1.2rem;

        &::before {
          content: "▹";
          position: absolute;
          left: 0;
          color: #d4af37;
        }
      }
    }
  }

  .left .timeline-content {
    margin-right: auto;
    margin-left: 0;

    .timeline-highlights li {
      padding-left: 1.2rem;
      padding-right: 0;

      &::before {
        left: 0;
      }
    }
  }

  .right .timeline-content {
    margin-left: auto;
    margin-right: 0;
  }

  .legacy-section {
    background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
    border-radius: 16px;
    padding: 3rem;
    margin-top: 3rem;

    .legacy-content {
      text-align: center;
      color: white;

      h3 {
        font-size: 1.8rem;
        margin-bottom: 1rem;
        font-weight: 700;
      }

      p {
        font-size: 1rem;
        line-height: 1.7;
        margin-bottom: 2rem;
        max-width: 700px;
        margin-left: auto;
        margin-right: auto;
        opacity: 0.9;
      }
    }

    .legacy-stats {
      display: flex;
      justify-content: center;
      gap: 3rem;
      flex-wrap: wrap;

      .stat {
        text-align: center;

        .stat-number {
          display: block;
          font-size: 2rem;
          font-weight: 800;
          color: #d4af37;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .timeline-content {
      width: calc(50% - 50px);
    }

    .timeline-marker {
      width: 70px;
    }
  }

  @media screen and (max-width: 900px) {
    .history-hero {
      height: 40vh;
      min-height: 350px;

      .hero-content h1 {
        font-size: 2.5rem;
      }
    }

    .history-container {
      padding: 3rem 1.5rem;
    }

    .timeline::before {
      left: 30px;
    }

    .timeline-item {
      &.left,
      &.right {
        .timeline-content {
          width: calc(100% - 80px);
          margin-left: 80px;
          text-align: left;

          ul {
            text-align: left;
          }
        }

        .timeline-marker {
          left: 0;
          transform: translateX(0);
          flex-direction: row;
          gap: 0.5rem;
          width: auto;

          .timeline-icon {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    .history-hero .hero-content h1 {
      font-size: 2rem;
    }

    .history-hero .hero-content p {
      font-size: 1rem;
    }

    .intro-section h2 {
      font-size: 1.5rem;
    }

    .intro-section p {
      font-size: 0.95rem;
    }

    .timeline-content {
      padding: 1rem;

      h3 {
        font-size: 1.1rem;
      }
    }

    .legacy-section {
      padding: 2rem;

      .legacy-content h3 {
        font-size: 1.4rem;
      }

      .legacy-stats {
        gap: 1.5rem;

        .stat .stat-number {
          font-size: 1.5rem;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .history-hero {
      height: 35vh;
      min-height: 300px;
    }

    .history-container {
      padding: 2rem 1rem;
    }

    .timeline-item {
      &.left,
      &.right {
        .timeline-content {
          width: calc(100% - 70px);
          margin-left: 70px;
        }
      }
    }

    .timeline-marker .timeline-icon {
      width: 35px;
      height: 35px;
      font-size: 0.9rem;
    }

    .legacy-section {
      padding: 1.5rem;

      .legacy-stats {
        flex-direction: column;
        gap: 1rem;
      }
    }
  }

  @media screen and (max-width: 480px) {
    .history-hero .hero-content h1 {
      font-size: 1.5rem;
    }

    .intro-section h2 {
      font-size: 1.3rem;
    }

    .timeline-content .timeline-highlights li {
      font-size: 0.8rem;
    }
  }
`;

export default History;
